import { getFileTypeFromContentType } from 'src/components/AddressFilesDropzone/utils';
import { config } from 'src/core/config';
import type { ConversationAttachment } from 'src/graphql/entities/conversation/conversation.types';
import type { MoveAttachmentToSiteFilesAttachment } from 'src/graphql/entities/files-upload.types';
import { getMoveAttachmentToSiteFilesPayload } from 'src/graphql/entities/files-upload.utils';

export type AttachmentKind = 'image' | 'pdf' | 'document' | 'other';

export interface AttachmentPillModel {
  id: string;
  siteFilePayload: MoveAttachmentToSiteFilesAttachment;
  displayDate: string;
  fileName: string;
  kind: AttachmentKind;
  url?: string;
}

const DOCUMENT_FILE_TYPES = ['doc', 'txt'];

const attachmentDateFormatter = Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export const getConversationAttachmentUrl = (
  attachment: Pick<ConversationAttachment, 'conversationId' | 'fileKey' | 'partnerId'>,
): string | undefined => {
  if (!config.s3AttachmentsUrl || !attachment.fileKey) {
    return undefined;
  }

  return `${config.s3AttachmentsUrl}${attachment.partnerId}/${attachment.conversationId}/${attachment.fileKey}`;
};

export const getAttachmentKind = (contentType?: string | null): AttachmentKind => {
  const fileType = getFileTypeFromContentType(contentType ?? '');

  if (fileType === 'img') {
    return 'image';
  }

  if (fileType === 'pdf') {
    return 'pdf';
  }

  if (DOCUMENT_FILE_TYPES.includes(fileType)) {
    return 'document';
  }

  return 'other';
};

export const mapConversationAttachmentPills = (
  attachments: ConversationAttachment[],
): AttachmentPillModel[] => {
  return attachments
    .filter(
      (a) =>
        typeof a.conversationAttachmentId === 'string' &&
        typeof a.fileName === 'string' &&
        typeof a.createdAt === 'string' &&
        !Number.isNaN(new Date(a.createdAt).getTime()),
    )
    .toSorted(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    )
    .map((attachment) => ({
      id: attachment.conversationAttachmentId,
      siteFilePayload: getMoveAttachmentToSiteFilesPayload(attachment),
      displayDate: attachmentDateFormatter.format(new Date(attachment.createdAt)),
      fileName: attachment.fileName,
      kind: getAttachmentKind(attachment.contentType),
      url: getConversationAttachmentUrl(attachment),
    }));
};
